/* eslint-disable no-console */
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Box, Container, Grid, Paper, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createCompanyFormFields } from '../../../forms/fields/formFields/createCompany';
import { createCompanySchema } from '../../../forms/validationSchema/formSchema/createCompany';
import { useAppDispatch } from '../../../redux/hooks';
import {
  createCompany,
  fetchCompany,
  updateCompany,
} from '../../../redux/modules/b2bThriftPlan/companies/company.actions';
import { locations } from '../../../routes/locations';
import {
  companyCreateLoadingSelector,
  companySelector,
  companyUpdateLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/companies/company.selectors';
import { CreateCompanyRequest, CreateCompanyResponse } from '../../../types/b2tRatibiPlus';
import { CompanyAddressesTable } from './CompanyAddressesTable';

export const CreateCompany = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { company } = useSelector(companySelector);
  const isUpdateLoading = useSelector(companyUpdateLoadingSelector);
  const isCreateLoading = useSelector(companyCreateLoadingSelector);
  const isEdit = id !== 'create';
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  const { handleSubmit, values, handleChange, handleBlur, touched, errors, resetForm, isValid, dirty, setFieldValue } =
    useFormik({
      initialValues: {
        [createCompanyFormFields.crNumber]: '',
        [createCompanyFormFields.crExpDate]: '',
        [createCompanyFormFields.owner]: '',
        [createCompanyFormFields.phoneNumber]: '',
        [createCompanyFormFields.email]: '',
        [createCompanyFormFields.name]: '',
        [createCompanyFormFields.nameAr]: '',
        [createCompanyFormFields.vatNumber]: '',
      },
      validationSchema: createCompanySchema,
      validateOnMount: true,
      onSubmit: async (formValues) => {
        if (isEdit) {
          try {
            // Get only changed fields
            const changedFields: Record<string, any> = {};
            Object.keys(formValues).forEach((key) => {
              const currentValue = formValues[key];
              const initialValue = initialValues[key];
              // Normalize values for comparison (handle empty strings and null/undefined)
              const normalizedCurrent = currentValue === '' ? null : currentValue;
              const normalizedInitial = initialValue === '' ? null : initialValue;
              if (normalizedCurrent !== normalizedInitial) {
                changedFields[key] = currentValue;
              }
            });

            if (changedFields.crNumber !== undefined) {
              changedFields.crNumber = String(changedFields.crNumber);
            }
            await dispatch(updateCompany({ id: Number(id), ...changedFields })).unwrap();
            enqueueSnackbar('Company updated!', { variant: 'success' });
          } catch (error: any) {
            const errorMessage = error?.message || 'Company update error!';
            enqueueSnackbar(errorMessage, { variant: 'error' });
          }
        } else {
          // Create company first
          try {
            const res = (await dispatch(
              createCompany({
                ...formValues,
                crNumber: String(formValues.crNumber),
                address: '',
              } as CreateCompanyRequest),
            ).unwrap()) as CreateCompanyResponse;
            const companyId = res?.data?.id;
            enqueueSnackbar('Company created!', { variant: 'success' });
            navigate(locations.b2bThriftPlan(companyId));
          } catch (error: any) {
            const errorMessage = error?.message || 'Company creation error!';
            enqueueSnackbar(errorMessage, { variant: 'error' });
          }
        }
      },
    });
  console.log(errors, isValid, dirty);
  useEffect(() => {
    if (isEdit) {
      dispatch(fetchCompany({ companyId: Number(id) }));
    }
  }, [id]);
  const onResetForm = () => {
    if (isEdit) {
      const initialFormValues = {
        [createCompanyFormFields.crNumber]: company.crNumber || '',
        [createCompanyFormFields.crExpDate]: company.crExpDate || '',
        [createCompanyFormFields.owner]: company.owner || '',
        [createCompanyFormFields.name]: company.name || '',
        [createCompanyFormFields.nameAr]: (company as any).nameAr || '',
        [createCompanyFormFields.vatNumber]: (company as any).vatNumber || '',
        [createCompanyFormFields.phoneNumber]: company.phoneNumber || '',
        [createCompanyFormFields.email]: company?.companyAdmins?.[0]?.email || '',
      };
      setInitialValues(initialFormValues);
      resetForm({
        values: initialFormValues,
      });
    } else {
      setInitialValues({});
      resetForm();
    }
  };
  useEffect(() => {
    onResetForm();
  }, [isEdit, company]);
  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">{isEdit ? 'Edit Company' : 'Create Company'}</Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyFormFields.name}
              name={createCompanyFormFields.name}
              label="Name"
              value={values[createCompanyFormFields.name]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && !!errors.name}
              helperText={touched.name && errors.name}
            />
          </Grid>
          {/* Arabic Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyFormFields.nameAr}
              name={createCompanyFormFields.nameAr}
              label="Name (Arabic)"
              value={values[createCompanyFormFields.nameAr]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.nameAr && !!errors.nameAr}
              helperText={touched.nameAr && errors.nameAr}
            />
          </Grid>
          {/* Owner */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyFormFields.owner}
              name={createCompanyFormFields.owner}
              label="Owner"
              value={values[createCompanyFormFields.owner]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.owner && !!errors.owner}
              helperText={touched.owner && errors.owner}
            />
          </Grid>
          {/* CR Number - digits only input, sent as string to BE */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyFormFields.crNumber}
              name={createCompanyFormFields.crNumber}
              label="CR Number"
              type="text"
              inputMode="numeric"
              inputProps={{ pattern: '[0-9]*', maxLength: 10 }}
              value={values[createCompanyFormFields.crNumber]}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '');
                setFieldValue(createCompanyFormFields.crNumber, digitsOnly);
              }}
              onBlur={handleBlur}
              error={touched.crNumber && !!errors.crNumber}
              helperText={touched.crNumber && errors.crNumber}
            />
          </Grid>
          {/* VAT Number */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyFormFields.vatNumber}
              name={createCompanyFormFields.vatNumber}
              label="VAT Number"
              value={values[createCompanyFormFields.vatNumber]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.vatNumber && !!errors.vatNumber}
              helperText={touched.vatNumber && errors.vatNumber}
            />
          </Grid>
          {/* CR Expire Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyFormFields.crExpDate}
              name={createCompanyFormFields.crExpDate}
              label="CR Expiration Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={values[createCompanyFormFields.crExpDate]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.crExpDate && !!errors.crExpDate}
              helperText={touched.crExpDate && errors.crExpDate}
            />
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyFormFields.phoneNumber}
              name={createCompanyFormFields.phoneNumber}
              label="Phone Number"
              value={values[createCompanyFormFields.phoneNumber]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.phoneNumber && !!errors.phoneNumber}
              helperText={touched.phoneNumber && errors.phoneNumber}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyFormFields.email}
              name={createCompanyFormFields.email}
              label="Email"
              value={values[createCompanyFormFields.email]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <LoadingButton
              type="submit"
              color="primary"
              variant="contained"
              sx={{ mt: 3, mb: 2, mr: 2 }}
              endIcon={<SaveIcon />}
              loading={isUpdateLoading || isCreateLoading}
              loadingPosition="end"
              disabled={!isValid || !dirty || isUpdateLoading || isCreateLoading}
            >
              {isEdit ? 'Save' : 'Create'}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>

      {/* Addresses section - only on edit page (after navigate from create) */}
      <Box sx={{ mt: 4 }}>
        {isEdit && Number(id) ? (
          <CompanyAddressesTable companyId={Number(id)} />
        ) : (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Addresses
            </Typography>
            <Typography color="text.secondary">Create or save the company first to add addresses.</Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};
